import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Loader2,
  CheckCircle2,
  Sun,
  Sunset,
  Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  property_type: "",
  service_type: "",
  window_count: "",
  preferred_contact_time: "",
  message: "",
};

// Values sent to the backend stay in French (owner reads French); labels are translated.
const contactTimeValues = ["AM (9h - 12h)", "Début PM (13h - 17h)", "Fin PM (17h - 18h)"];
const contactTimeIcons = [Sun, Sunset, Moon];
const windowValues = ["1-3", "4-8", "9-15", "16+"];

// Formulaire d'estimation, utilisé sur /contact et dans le CTA final de l'accueil.
export default function EstimateForm({ submitClassName }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t, lang } = useLanguage();
  const f = t("contact.form");

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.property_type) {
      toast.error(f.errorRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, { ...form, lang });
      setSubmitted(true);
      toast.success(f.successToast);
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || f.errorToast;
      toast.error(typeof msg === "string" ? msg : f.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return <SuccessCard onReset={() => setSubmitted(false)} />;
  }

  return (
    <form onSubmit={submit} data-testid="estimate-form" className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Field label={f.name} htmlFor="name">
          <Input
            id="name"
            data-testid="input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={f.namePlaceholder}
            className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600"
            required
          />
        </Field>
        <Field label={f.email} htmlFor="email">
          <Input
            id="email"
            data-testid="input-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder={f.emailPlaceholder}
            className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600"
            required
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label={f.phone} htmlFor="phone">
          <Input
            id="phone"
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder={f.phonePlaceholder}
            className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600"
            required
          />
        </Field>
        <Field label={f.address} htmlFor="address">
          <Input
            id="address"
            data-testid="input-address"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
            placeholder={f.addressPlaceholder}
            className="rounded-xl bg-white h-12 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600"
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label={f.propertyType} htmlFor="property_type">
          <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
            <SelectTrigger data-testid="select-property-type" className="rounded-xl bg-white h-12 border-slate-200">
              <SelectValue placeholder={f.select} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residentiel">{f.residential}</SelectItem>
              <SelectItem value="commercial">{f.commercial}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={f.windowCount} htmlFor="window_count">
          <Select value={form.window_count} onValueChange={(v) => update("window_count", v)}>
            <SelectTrigger data-testid="select-window-count" className="rounded-xl bg-white h-12 border-slate-200">
              <SelectValue placeholder={f.select} />
            </SelectTrigger>
            <SelectContent>
              {windowValues.map((v, i) => (
                <SelectItem key={v} value={v}>{f.windows[i]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label={f.bestTime} htmlFor="preferred_contact_time">
        <div className="grid grid-cols-3 gap-3">
          {contactTimeValues.map((value, i) => {
            const opt = f.times[i];
            const selected = form.preferred_contact_time === value;
            const Icon = contactTimeIcons[i];
            return (
              <button
                key={value}
                type="button"
                data-testid={`time-${opt.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => update("preferred_contact_time", selected ? "" : value)}
                className={`p-4 rounded-xl border transition-all text-left ${
                  selected
                    ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-black/5"
                    : "bg-white border-slate-200 text-slate-900 hover:border-blue-400 hover:bg-blue-50"
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${selected ? "text-white" : "text-blue-600"}`} />
                <div className="font-medium text-sm">{opt.label}</div>
                <div className={`text-xs mt-0.5 ${selected ? "text-blue-100" : "text-slate-500"}`}>{opt.sub}</div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={f.message} htmlFor="message">
        <Textarea
          id="message"
          data-testid="input-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder={f.messagePlaceholder}
          rows={4}
          className="rounded-xl bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600 resize-none"
        />
      </Field>

      <Button
        type="submit"
        data-testid="submit-estimate"
        disabled={loading}
        className={`w-full rounded-full ${submitClassName || "bg-blue-600 hover:bg-blue-700"} text-white h-14 px-8 text-base font-medium shadow-md shadow-black/5`}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" /> {f.sending}
          </>
        ) : (
          <>
            {f.submit} <ArrowRight className="ml-2 w-4 h-4" />
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500">{f.legal}</p>
    </form>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-800">{label}</Label>
      {children}
    </div>
  );
}

function SuccessCard({ onReset }) {
  const { t } = useLanguage();
  const f = t("contact.form");
  return (
    <div data-testid="submit-success" className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 grid place-items-center mx-auto">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h3 className="font-heading text-3xl font-normal text-slate-900 mt-6">{f.successTitle}</h3>
      <p className="mt-4 text-slate-600 max-w-md mx-auto">{f.successText}</p>
      <Button
        onClick={onReset}
        data-testid="submit-another"
        variant="outline"
        className="mt-8 rounded-full border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900"
      >
        {f.successAnother}
      </Button>
    </div>
  );
}
